import { createTheme, type Theme } from '@mui/material/styles';

/**
 * Every color role the public site's look is built from, mirrored exactly
 * from the backend's `ThemeColors` record — the admin form, the API and this
 * factory all agree on the same 14 named fields, so a role can never go
 * missing silently between them.
 */
export interface ThemeColors {
  primaryColor: string;
  secondaryColor: string;
  successColor: string;
  errorColor: string;
  backgroundColor: string;
  surfaceColor: string;
  headerBackgroundColor: string;
  headerTextColor: string;
  /**
   * The site title's subtitle, rendered right next to the title. Its own
   * role, independent of both `headerTextColor` (the header's own text) and
   * `headingColor` (h1/h2/h3 page titles, an unrelated role despite the
   * similar name) — a highly visible, brand-specific element that must not
   * silently recolor if either of those changes.
   */
  headerSubtitleColor: string;
  footerBackgroundColor: string;
  footerTextColor: string;
  textPrimaryColor: string;
  textSecondaryColor: string;
  headingColor: string;
  quoteColor: string;
  /**
   * The far end of the landing hero's background gradient (it starts from
   * `primaryColor`). Its own role rather than reusing `footerBackgroundColor`
   * — the two used to be the same value, which meant giving the footer its
   * own background silently changed the hero too.
   */
  heroGradientEndColor: string;
}

/**
 * The look the site shipped with, before any administrator changed anything —
 * identical to the seeded "Default" theme row on the backend
 * (`V4__site_theme.sql`), so a first paint before the live theme has loaded
 * matches what the backend would return anyway.
 */
export const DEFAULT_THEME_COLORS: ThemeColors = {
  primaryColor: '#0F766E',
  secondaryColor: '#F59E0B',
  successColor: '#15803D',
  errorColor: '#B91C1C',
  backgroundColor: '#FBFAF7',
  surfaceColor: '#FFFFFF',
  headerBackgroundColor: '#FFFFFF',
  headerTextColor: '#1F2937',
  headerSubtitleColor: '#1F2937',
  footerBackgroundColor: '#0A544E',
  footerTextColor: '#FFFFFF',
  textPrimaryColor: '#1F2937',
  textSecondaryColor: '#4B5563',
  headingColor: '#1F2937',
  quoteColor: '#4B5563',
  heroGradientEndColor: '#0A544E',
};

// MUI's own palette has no header/footer/quote concept — these three are
// genuinely new roles this app needed, so they are declared as a `custom`
// namespace rather than overloaded onto an existing, more ambiguous slot.
declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      header: { background: string; text: string; subtitle: string };
      footer: { background: string; text: string };
      quote: string;
      hero: { gradientEnd: string };
    };
  }

  interface ThemeOptions {
    custom?: {
      header?: { background?: string; text?: string; subtitle?: string };
      footer?: { background?: string; text?: string };
      quote?: string;
      hero?: { gradientEnd?: string };
    };
  }
}

/**
 * Builds the MUI theme for a given set of colors.
 *
 * <p>One color per role rather than every palette depth (light/dark/contrast
 * text) — MUI's own `augmentColor` derives the rest from the "main" tone,
 * the same way this file always relied on it implicitly for success/error.
 */
export function createSiteTheme(colors: ThemeColors): Theme {
  return createTheme({
    palette: {
      primary: { main: colors.primaryColor, contrastText: '#FFFFFF' },
      secondary: { main: colors.secondaryColor },
      success: { main: colors.successColor },
      error: { main: colors.errorColor },
      background: {
        default: colors.backgroundColor,
        paper: colors.surfaceColor,
      },
      text: {
        primary: colors.textPrimaryColor,
        secondary: colors.textSecondaryColor,
      },
    },

    shape: { borderRadius: 12 },

    typography: {
      fontFamily: ['"Inter"', '"Segoe UI"', 'Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      h1: { fontSize: 'clamp(2rem, 5vw, 3.25rem)', fontWeight: 700, lineHeight: 1.15, color: colors.headingColor },
      h2: { fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)', fontWeight: 700, lineHeight: 1.2, color: colors.headingColor },
      h3: { fontSize: 'clamp(1.25rem, 2.5vw, 1.6rem)', fontWeight: 600, color: colors.headingColor },
      button: { textTransform: 'none', fontWeight: 600 },
    },

    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 999, paddingInline: '1.4rem' } },
      },
      MuiCard: {
        styleOverrides: {
          root: { border: `1px solid ${hexToRgba(colors.primaryColor, 0.12)}` },
        },
      },
    },

    custom: {
      header: {
        background: colors.headerBackgroundColor,
        text: colors.headerTextColor,
        subtitle: colors.headerSubtitleColor,
      },
      footer: { background: colors.footerBackgroundColor, text: colors.footerTextColor },
      quote: colors.quoteColor,
      hero: { gradientEnd: colors.heroGradientEndColor },
    },
  });
}

/** `#RRGGBB` (or `#RGB`) to an `rgba(...)` string, for the one border tint that needs alpha. */
function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.length === 4
    ? `#${[...hex.slice(1)].map((c) => c + c).join('')}`
    : hex;

  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** The theme built from the default colors, for anywhere a theme is needed before data has loaded. */
export const theme = createSiteTheme(DEFAULT_THEME_COLORS);

export default theme;
